import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "./Navbar";
import { useLang } from "../i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGR1c2slMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzkyNDM5NDI3fDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const STATS = [
    { value: "20+", label: t("Anni di esperienza", "Years of experience") },
    { value: "100%", label: t("Chiavi in mano", "Turnkey") },
    { value: "360°", label: t("Dalla progettazione alla consegna", "From design to delivery") },
  ];

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <img
          src={HERO_IMG}
          alt={t("Villa moderna al tramonto realizzata da Adriatica", "Modern villa at dusk built by Adriatica")}
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070D18] via-[#070D18]/80 to-[#070D18]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070D18] via-transparent to-[#070D18]/60" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto w-full px-6 lg:px-10 pt-32 pb-20"
      >
        <div className="max-w-3xl">
          <span className="block overflow-hidden mb-6">
            <motion.span
              className="inline-flex items-center gap-3 text-[11px] sm:text-xs uppercase tracking-[0.35em] font-semibold text-[#D4AF37]"
              initial={{ y: "120%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-10 h-px bg-[#D4AF37]" />
              Adriatica S.r.l. Semplificata — Bologna
            </motion.span>
          </span>

          <h1 className="font-display font-bold text-white leading-[0.98] tracking-tight text-5xl sm:text-6xl lg:text-8xl">
            <Line delay={0.3}>{t("COSTRUIAMO", "WE BUILD")}</Line>
            <Line delay={0.45}>
              <span className="font-serif italic font-medium text-gold-gradient">{t("il tuo futuro", "your future")}</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
            data-testid="hero-subtitle"
          >
            {t(
              "Impresa edile con oltre vent'anni di esperienza. Ristrutturazioni, nuove costruzioni e impianti: soluzioni su misura, dalla progettazione alla consegna.",
              "A construction company with over twenty years of experience. Renovations, new builds and systems: tailor-made solutions, from design to delivery."
            )}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contatti"
              data-testid="hero-cta-quote-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#contatti");
              }}
              className="group inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[#070D18] bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] px-7 py-3.5 rounded-full hover:shadow-[0_0_36px_rgba(212,175,55,0.5)] transition-shadow duration-300"
            >
              {t("Richiedi un preventivo", "Request a quote")}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#servizi"
              data-testid="hero-cta-services-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("#servizi");
              }}
              className="inline-flex items-center gap-2 text-sm sm:text-base font-medium text-[#F3E08B] border border-[#D4AF37]/50 px-7 py-3.5 rounded-full hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-colors duration-300"
            >
              {t("Esplora i servizi", "Explore our services")}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="relative z-10 border-t border-[#D4AF37]/15 backdrop-blur-md bg-[#070D18]/55"
        data-testid="hero-stats-bar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6 items-center">
          {STATS.map((s) => (
            <div key={s.label} className="flex items-baseline gap-3">
              <span className="font-display text-2xl sm:text-3xl text-gold-gradient font-bold">{s.value}</span>
              <span className="text-xs sm:text-sm text-slate-400 tracking-wide">{s.label}</span>
            </div>
          ))}
          <div className="hidden sm:flex absolute right-10 -translate-y-16">
            <ArrowDown className="text-[#D4AF37]/70 animate-bounce" size={20} />
          </div>
        </div>
      </motion.div>
    </section>
  );
};
